import React,{useEffect,useState} from "react";
import "./InterviewSessionPage.css";

const RECOMMENDATIONS=[["proceed_to_l2","Proceed to L2"],["proceed_to_l3","Proceed to L3"],["hr_round","Move to HR round"],["reject","Reject"],["hold","Hold"]];
const FINDING_LABEL={demonstrated:"Demonstrated",needs_follow_up:"Needs follow-up",not_established:"Not established",pending_review:"Pending review"};
const FINDING_CLASS={demonstrated:"good",needs_follow_up:"warn",not_established:"bad",pending_review:"muted"};
const formatWhen=value=>value?new Intl.DateTimeFormat(undefined,{dateStyle:"full",timeStyle:"short"}).format(new Date(value)):"";
const formatDay=value=>value?new Intl.DateTimeFormat(undefined,{month:"short",day:"numeric",hour:"2-digit",minute:"2-digit"}).format(new Date(value)):"";
export const recommendationLabel=value=>(RECOMMENDATIONS.find(([id])=>id===value)||[,""])[1]||String(value||"").replaceAll("_"," ");

export default function InterviewSessionPage({apiBase,organizationId,token}){
  const endpoint=apiBase+"/api/public/interview-sessions/"+encodeURIComponent(organizationId)+"/"+encodeURIComponent(token);
  const [session,setSession]=useState(null);
  const [error,setError]=useState("");
  const [notes,setNotes]=useState("");
  const [transcript,setTranscript]=useState("");
  const [recommendation,setRecommendation]=useState("");
  const [saving,setSaving]=useState(false);
  const [formError,setFormError]=useState("");
  const [notice,setNotice]=useState("");

  async function load(){
    const r=await fetch(endpoint);
    const d=await r.json().catch(()=>({}));
    if(!r.ok)throw new Error(typeof d.detail==="string"?d.detail:"Could not access this interview session.");
    setSession(d);
  }
  useEffect(()=>{setSession(null);setError("");load().catch(e=>setError(e.message));},[organizationId,token]);

  async function submit(){
    setSaving(true);setFormError("");setNotice("");
    try{
      const r=await fetch(endpoint+"/feedback",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({recommendation,notes,transcript})});
      const d=await r.json().catch(()=>({}));
      if(!r.ok)throw new Error(typeof d.detail==="string"?d.detail:"Could not submit your feedback.");
      setNotice(d.message||"Your feedback has been submitted.");
      await load();
    }catch(e){setFormError(e.message);}
    finally{setSaving(false);}
  }

  if(error)return <main className="session-public"><article className="session-panel"><small>AgenticHR · Interview session</small><h1>Session unavailable</h1><p role="alert">{error}</p></article></main>;
  if(!session)return <main className="session-public"><article className="session-panel"><small>AgenticHR · Interview session</small><h1>Opening your interview session…</h1></article></main>;

  const locked=session.submitted||session.completed;
  const assessment=session.assessment;
  const preparation=session.preparation||{};
  const feedback=session.feedback||{};
  const candidate=session.candidate||{};
  const skills=Array.isArray(candidate.skills)?candidate.skills:[];
  const levelLabel=session.interview_level==="HR"?"HR round":session.interview_level+" round";

  return <main className="session-public"><article className="session-panel">
    <small>AgenticHR · Interview session</small>
    <header className="session-head">
      <div>
        <h1>{session.candidate_name}</h1>
        <p>{session.role_title} · {levelLabel}</p>
      </div>
      <div className="session-when">
        <b>{formatWhen(session.starts_at)}</b>
        <span>{formatDay(session.starts_at)} – {formatDay(session.ends_at)}</span>
      </div>
    </header>
    <p className="session-intro">Prepared for {session.interviewer_email}. This private session is for your assigned interview only. AI notes summarize what the candidate said — make your own assessment during the interview.</p>
    {session.meeting_link&&<p className="session-meeting">Meeting link: <a href={session.meeting_link} target="_blank" rel="noopener noreferrer">{session.meeting_link}</a></p>}
    {session.notes&&<p className="session-notes"><b>From the hiring team:</b> {session.notes}</p>}

    <section className="session-block">
      <h2>Candidate profile</h2>
      <div className="session-facts">
        {candidate.location&&<div><small>Location</small><b>{candidate.location}</b></div>}
        {candidate.experience_years!==null&&candidate.experience_years!==undefined&&<div><small>Experience</small><b>{candidate.experience_years} years</b></div>}
        {candidate.notice_days!==null&&candidate.notice_days!==undefined&&<div><small>Notice period</small><b>{candidate.notice_days} days</b></div>}
      </div>
      {skills.length>0&&<div className="session-skills">{skills.map(skill=><span key={skill}>{skill}</span>)}</div>}
      {session.resume&&<a className="session-resume" href={endpoint+"/resume"} target="_blank" rel="noopener noreferrer">Open resume · {session.resume.filename}</a>}
    </section>

    {session.prior_interviews?.length>0&&<section className="session-block">
      <h2>Earlier interviews · {session.prior_interviews.length}</h2>
      {session.prior_interviews.map((prior,index)=><div className="session-prior" key={index}>
        <b>{prior.interview_level} · {prior.interviewer_email}</b>
        <span>{recommendationLabel(prior.feedback?.recommendation)||prior.status} · {formatDay(prior.feedback_submitted_at||prior.starts_at)}</span>
        {prior.feedback?.notes&&<p>{prior.feedback.notes}</p>}
      </div>)}
    </section>}

    {assessment&&<section className="session-block">
      <h2>AI assessment · {assessment.summary?.demonstrated??0} of {assessment.summary?.total??0} criteria demonstrated</h2>
      {(assessment.strengths?.length>0||assessment.concerns?.length>0)&&<div className="session-signal-columns">
        <div><b>Strengths</b><ul>{assessment.strengths?.length?assessment.strengths.map((item,index)=><li key={index}>{item}</li>):<li>No confirmed strengths yet.</li>}</ul></div>
        <div><b>Points to probe</b><ul>{assessment.concerns?.length?assessment.concerns.map((item,index)=><li key={index}>{item}</li>):<li>No open concerns.</li>}</ul></div>
      </div>}
      {assessment.questions?.length>0&&<details><summary>Question-by-question evidence</summary>
        {assessment.questions.map(question=><div className="session-question" key={question.id}>
          <h3>{question.text}</h3>
          {(question.criteria||[]).map((criterion,index)=><div className="session-criterion" key={index}>
            <b>{criterion.name}</b> <span className={"finding "+(FINDING_CLASS[criterion.finding]||"muted")}>{FINDING_LABEL[criterion.finding]||criterion.finding}</span>
            {criterion.evidence_excerpt&&<q>{criterion.evidence_excerpt}</q>}
            {criterion.follow_up_question&&<p className="session-followup"><b>Suggested follow-up:</b> {criterion.follow_up_question}</p>}
          </div>)}
        </div>)}
      </details>}
    </section>}

    {(preparation.topics?.length>0||preparation.questions?.length>0)&&<section className="session-block">
      <h2>Suggested topics · {levelLabel}</h2>
      {preparation.topics?.length>0&&<div className="session-skills topics">{preparation.topics.map(topic=><span key={topic}>{topic}</span>)}</div>}
      {preparation.questions?.length>0&&<ol className="session-questions">{preparation.questions.map((question,index)=><li key={index}>{question}</li>)}</ol>}
    </section>}

    <section className="session-feedback">
      <h2>Your feedback · {levelLabel}</h2>
      {locked
        ?<div className="session-locked" role="status">
          <p><b>{session.submitted?"Your feedback was submitted.":"This interview session is completed."}</b> The hiring team has been notified.</p>
          {feedback.recommendation&&<p>Recommendation: <b>{recommendationLabel(feedback.recommendation)}</b></p>}
          {feedback.notes&&<p className="session-locked-notes">{feedback.notes}</p>}
          {feedback.transcript_summary&&<div className="session-summary"><b>Transcript summary</b><p>{feedback.transcript_summary}</p></div>}
        </div>
        :<><label>Interview transcript <small>(optional — paste the transcript and a summary is generated for the hiring team)</small><textarea rows="7" value={transcript} onChange={e=>setTranscript(e.target.value)} placeholder="Paste the interview transcript here…"/></label>
          <label>Evidence and observations<textarea rows="8" value={notes} onChange={e=>setNotes(e.target.value)} placeholder="What did the candidate demonstrate? What needs follow-up?"/></label>
          <label>Recommendation<select value={recommendation} onChange={e=>setRecommendation(e.target.value)}><option value="">Choose a recommendation</option>{RECOMMENDATIONS.map(([value,label])=><option key={value} value={value}>{label}</option>)}</select></label>
          <button disabled={saving||notes.trim().length<10||!recommendation} onClick={submit}>{saving?"Submitting…":"Submit feedback"}</button>
          {notice&&<p role="status">{notice}</p>}
          {formError&&<p role="alert">{formError}</p>}</>}
    </section>
  </article></main>;
}
